import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiError } from "../../api";
import type { CurrentSlot, Turbine } from "../../types";
import { tg } from "../../telegram";
import { useT } from "../../i18n";
import TurbineActions from "../../components/TurbineActions";
// onEmergency lives on its own tab now — see /emergency route.

export default function Report() {
  const { t } = useT();
  const navigate = useNavigate();
  const [turbines, setTurbines] = useState<Turbine[] | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [slot, setSlot] = useState<CurrentSlot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const loadTurbines = () => {
    setError(null);
    api.get<Turbine[]>("/api/turbines")
      .then(list => {
        setTurbines(list);
        if (selectedId === null && list.length > 0) setSelectedId(list[0].id);
      })
      .catch((e: ApiError) => setError(e.message));
  };

  const loadSlot = (turbineId: number) => {
    setSlot(null);
    api.get<CurrentSlot>(`/api/me/current-slot?turbine_id=${turbineId}`)
      .then(setSlot)
      .catch((e: ApiError) => setError(e.message));
  };

  useEffect(loadTurbines, []);
  useEffect(() => {
    if (selectedId !== null) loadSlot(selectedId);
  }, [selectedId]);

  const selected = turbines?.find(t => t.id === selectedId) ?? null;
  // Disabled only when the user is on day-off or the turbine is fully
  // deactivated. A stopped (but active) turbine is allowed — the row is
  // flagged offline and shown in orange.
  const planDisabled = !slot?.has_slot || (slot?.is_day_off ?? false);
  const isOffline = !!(slot && slot.has_slot && !slot.is_turbine_running);

  const onFillReport = () => {
    if (planDisabled || !selected) return;
    tg()?.HapticFeedback?.impactOccurred("light");
    navigate(`/report/fill?turbine_id=${selected.id}`);
  };

  const askToggleDayOff = () => {
    if (!slot?.has_shift || busy) return;
    const turningOn = !slot.is_day_off;
    const question = turningOn
      ? t("report.day_off_confirm_on")
      : t("report.day_off_confirm_off");
    const tgWeb = tg();
    if (tgWeb?.showConfirm) {
      tgWeb.showConfirm(question, (ok) => { if (ok) toggleDayOff(); });
    } else if (window.confirm(question)) {
      toggleDayOff();
    }
  };

  const toggleDayOff = async () => {
    setBusy(true);
    tg()?.HapticFeedback?.impactOccurred("medium");
    try {
      const fresh = await api.post<CurrentSlot>("/api/me/day-off");
      // Keep the slot info; reload for current turbine so .is_day_off updates.
      if (selectedId !== null) loadSlot(selectedId);
      void fresh;
      tg()?.HapticFeedback?.notificationOccurred("success");
    } catch (e) {
      setError((e as ApiError).message);
      tg()?.HapticFeedback?.notificationOccurred("error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <h1>{t("report.title")}</h1>

      {error && (
        <div className="alert error">
          <div style={{ marginBottom: 8 }}>⚠ {error}</div>
          <button className="btn secondary" onClick={loadTurbines}>{t("common.retry")}</button>
        </div>
      )}

      {!turbines && !error && (
        <div className="empty-state" style={{ padding: 60 }}>
          <span className="spinner" />
        </div>
      )}

      {turbines && turbines.length === 0 && (
        <div className="empty-state">{t("turbines.empty_for_station")}</div>
      )}

      {turbines && turbines.length > 0 && (
        <>
          {/* Turbine switcher */}
          <div className="turbine-switcher">
            {turbines.map(tb => (
              <button
                key={tb.id}
                className={"turbine-chip" + (tb.id === selectedId ? " active" : "")}
                onClick={() => { tg()?.HapticFeedback?.selectionChanged?.(); setSelectedId(tb.id); }}
              >
                <span className="turbine-chip-name">{tb.name}</span>
                <span className={"turbine-dot " + (tb.is_running ? "running" : tb.is_active ? "stopped" : "off")} />
              </button>
            ))}
          </div>

          {/* Selected turbine + status: big colored indicator + larger text. */}
          {selected && (() => {
            const stateClass = !selected.is_active
              ? "off"
              : selected.is_running ? "running" : "stopped";
            const stateText = !selected.is_active
              ? t("turbines.status_off")
              : selected.is_running ? t("turbines.status_running") : t("turbines.status_stopped");
            return (
              <div className={`turbine-status-card ${stateClass}`}>
                <span className="turbine-status-indicator" aria-hidden="true" />
                <div className="turbine-status-info">
                  <div className="turbine-status-name">{selected.name}</div>
                  <div className="turbine-status-state">{stateText}</div>
                  {slot && slot.is_day_off && (
                    <div className="turbine-status-hint">{t("report.day_off_hint")}</div>
                  )}
                  {slot && !slot.is_day_off && slot.has_slot && (
                    <div className="turbine-status-hint">{t("report.ready_to_fill")}</div>
                  )}
                  {slot && !slot.is_day_off && !slot.has_slot && (
                    <div className="turbine-status-hint">
                      {slot.message ?? t("report.no_slot_hint")}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          <div className="report-actions">
            <button
              className={"big-btn " + (isOffline ? "warning" : "primary")}
              onClick={onFillReport}
              disabled={planDisabled}
            >
              <span className="big-btn-icon">{isOffline ? "⚠" : "📋"}</span>
              <span className="big-btn-text">
                <b>{isOffline ? t("report.fill_btn_offline") : t("report.fill_btn")}</b>
                <small>
                  {slot?.is_day_off ? t("report.fill_sub_day_off")
                    : planDisabled ? t("report.fill_sub_disabled")
                    : isOffline ? t("report.fill_sub_offline")
                    : t("report.fill_sub_active")}
                </small>
              </span>
            </button>

            {selected && (
              <TurbineActions
                turbine={selected}
                onChanged={() => { loadTurbines(); if (selectedId !== null) loadSlot(selectedId); }}
              />
            )}

            {slot?.has_shift && (
              <button
                className={slot.is_day_off ? "big-btn primary" : "big-btn danger-outline"}
                onClick={askToggleDayOff}
                disabled={busy}
              >
                <span className="big-btn-icon">{slot.is_day_off ? "🔄" : "🌴"}</span>
                <span className="big-btn-text">
                  <b>{busy ? "…" : slot.is_day_off ? t("report.day_off_btn_on") : t("report.day_off_btn_off")}</b>
                  <small>
                    {slot.is_day_off ? t("report.day_off_sub_on") : t("report.day_off_sub_off")}
                  </small>
                </span>
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
